import { formatLongDate } from '@/lib/format-long-date';
import { Text, VStack } from '@chakra-ui/react';
import type { DisplayRightsProposalSummary } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { displayRightsTermsSummary } from './display-rights-terms-summary';

interface DisplayRightsTermsLineProps {
  proposal: DisplayRightsProposalSummary | null;
  /** Version is worth naming where the badge does not already carry it. */
  showVersion?: boolean;
}

/**
 * The terms in force, in one line: the money, the run time, the version, and —
 * when the launch rested on it — who affirmed an off-platform agreement and
 * when.
 *
 * One component for the three surfaces that state the conditions (mention
 * panel, contact card, co-publicites index), because a brand reading "75 € /
 * month · v2" in one place and a different sentence in another has no way to
 * know it is the same contract.
 *
 * Renders nothing when nothing was ever proposed: an empty conditions line is
 * worse than no line, it suggests terms that failed to load.
 */
export function DisplayRightsTermsLine({
  proposal,
  showVersion = false,
}: DisplayRightsTermsLineProps) {
  const { t, i18n } = useTranslation();

  if (!proposal) return null;

  const terms = displayRightsTermsSummary(proposal, i18n.language, t);
  if (!terms) return null;

  const version =
    showVersion && proposal.versionNumber > 1
      ? t('displayRights.terms.version', { version: proposal.versionNumber })
      : null;

  const attestation = proposal.offPlatformAttestedAt
    ? t(
        proposal.offPlatformAttestedByName
          ? 'displayRights.terms.attestedBy'
          : 'displayRights.terms.attested',
        {
          name: proposal.offPlatformAttestedByName,
          date: formatLongDate(proposal.offPlatformAttestedAt, i18n.language),
        }
      )
    : null;

  return (
    <VStack align="start" gap={0.5} minW="0" data-testid="display-rights-terms-line">
      <Text fontSize="11px" color="primary.500">
        {version ? `${terms} · ${version}` : terms}
      </Text>
      {attestation && (
        <Text fontSize="11px" color="fg.muted" data-testid="display-rights-attestation">
          {attestation}
        </Text>
      )}
    </VStack>
  );
}
